import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HeartPulse, LogIn, Wand2, Mail, Euro } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = [
    { key: "all", label: "Alle" },
    { key: "no_edits", label: "0 bewerkingen", hint: "Drempel bij het starten → onboarding", color: "text-red-600" },
    { key: "tried_inactive", label: "Geprobeerd, daarna inactief", hint: "≥1 bewerking, 7+ dagen stil → reactivatie", color: "text-amber-600" },
    { key: "credits_gone", label: "Credits op, geen aankoop", hint: "Prijs of waarde → feedback vragen", color: "text-purple-600" },
    { key: "active", label: "Actief", hint: "Recent nog bewerkingen gedaan", color: "text-emerald-600" },
    { key: "paying", label: "Betalend", hint: "Uitgesloten van de herinneringsreeks", color: "text-blue-600" },
];

const MAIL_STEPS = [
    ["activation_nudge_sent_at", "D3"],
    ["tried_nudge_sent_at", "D7"],
    ["reactivation_sent_at", "D21"],
    ["feedback_mail_sent_at", "FB"],
];

const fmt = (iso) => {
    if (!iso) return "—";
    if (!iso.includes("T")) return iso;
    const d = new Date(iso);
    return d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export default function AdminRetention() {
    const [data, setData] = useState(null);
    const [seg, setSeg] = useState("all");

    useEffect(() => {
        api.get("/admin/retention").then((r) => setData(r.data)).catch(() => toast.error("Retentiedata laden mislukt"));
    }, []);

    const rows = useMemo(() => {
        if (!data) return [];
        return seg === "all" ? data.users : data.users.filter((u) => u.segment === seg);
    }, [data, seg]);

    if (!data) {
        return <div className="min-h-screen bg-zinc-50 p-10 text-sm text-zinc-500" data-testid="retention-loading">Laden…</div>;
    }

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950 pb-20" data-testid="admin-retention-page">
            <div className="max-w-6xl mx-auto px-6 pt-10">
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6">
                    <ArrowLeft className="h-4 w-4" /> Dashboard
                </Link>
                <div className="flex items-center gap-3 mb-2">
                    <HeartPulse className="h-6 w-6 text-[#E5A93D]" />
                    <h1 className="font-display font-black text-3xl tracking-tight">Retentie</h1>
                </div>
                <p className="text-sm text-zinc-500 mb-8">
                    Automatische reeks: welkomstmail (dag 0) → dag 3 "nog geen foto bewerkt" → dag 7 "andere toepassing" → dag 21 reactivatie.
                    Stopt vanzelf bij activiteit; betalende klanten worden overgeslagen.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
                    {SEGMENTS.filter((s) => s.key !== "all").map((s) => (
                        <button key={s.key} onClick={() => setSeg(seg === s.key ? "all" : s.key)}
                            className={`text-left p-4 bg-white border rounded-sm transition hover:border-zinc-400 ${seg === s.key ? "border-[#E5A93D] ring-1 ring-[#E5A93D]" : "border-zinc-200"}`}
                            title={s.hint} data-testid={`retention-segment-${s.key}`}>
                            <div className={`font-display font-black text-3xl ${s.color}`}>{data.counts[s.key] ?? 0}</div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
                        </button>
                    ))}
                </div>

                <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm min-w-[820px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                                <th className="px-4 py-3">Makelaar</th>
                                <th className="px-4 py-3"><span className="inline-flex items-center gap-1"><LogIn className="h-3 w-3" />Laatste login</span></th>
                                <th className="px-4 py-3"><span className="inline-flex items-center gap-1"><Wand2 className="h-3 w-3" />Bewerkingen</span></th>
                                <th className="px-4 py-3">Laatste bewerking</th>
                                <th className="px-4 py-3">Credits</th>
                                <th className="px-4 py-3"><span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />Reeks</span></th>
                                <th className="px-4 py-3">Segment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => {
                                const segMeta = SEGMENTS.find((s) => s.key === u.segment);
                                return (
                                    <tr key={u.id} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`retention-row-${u.id}`}>
                                        <td className="px-4 py-3">
                                            <Link to={`/admin/users/${u.id}`} className="font-bold hover:text-[#C8922C]">{u.name || u.email}</Link>
                                            <div className="text-xs text-zinc-400">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-3 text-zinc-600">{fmt(u.last_login_at)}</td>
                                        <td className="px-4 py-3 font-bold">{u.edits}</td>
                                        <td className="px-4 py-3 text-zinc-600">{fmt(u.last_edit_at)}</td>
                                        <td className="px-4 py-3 text-zinc-600">{u.credits_balance ?? 0}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1">
                                                {MAIL_STEPS.map(([f, lbl]) => {
                                                    const v = u[f];
                                                    const sent = v && v.includes("T");
                                                    const skipped = v && !v.includes("T");
                                                    return (
                                                        <span key={f} title={sent ? `Verstuurd ${fmt(v)}` : skipped ? v : "Nog niet"}
                                                            className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm border ${sent ? "bg-emerald-50 text-emerald-700 border-emerald-200" : skipped ? "bg-zinc-100 text-zinc-400 border-zinc-200 line-through" : "bg-white text-zinc-300 border-zinc-100"}`}>
                                                            {lbl}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 text-xs font-bold ${segMeta?.color || "text-zinc-500"}`}>
                                                {u.paying && <Euro className="h-3 w-3" />}{segMeta?.label || u.segment}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Geen makelaars in dit segment.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
